import { useCallback, useState } from 'react';
import type {
  ChatChoiceOptionRecord,
  ChatChoicePromptBlock,
  ChatConfirmationPromptBlock,
  ChatFormFieldRecord,
  ChatFormPromptBlock,
  ChatInputPromptBlock,
  ChatInteractRequestContentBlock,
  ChatMessageContentEvent,
  PromptChoiceDraftMap,
  PromptFormDraftMap,
  PromptInputDraftMap,
  PromptSubmissionStateMap
} from './ChatMessageContentBlocks';
import { getPromptKey, getPromptMessageId } from './ChatMessageContentBlocks';

interface UseChatPromptInteractionsOptions {
  submitBlocks: (
    messageId: string | undefined,
    questionId: string | undefined,
    blockType: string | undefined,
    content: ChatInteractRequestContentBlock[]
  ) => Promise<void>;
}

export const useChatPromptInteractions = ({ submitBlocks }: UseChatPromptInteractionsOptions) => {
  const [promptChoiceDrafts, setPromptChoiceDrafts] = useState<PromptChoiceDraftMap>({});
  const [promptInputDrafts, setPromptInputDrafts] = useState<PromptInputDraftMap>({});
  const [promptFormDrafts, setPromptFormDrafts] = useState<PromptFormDraftMap>({});
  const [promptSubmissionState, setPromptSubmissionState] = useState<PromptSubmissionStateMap>({});

  const submitPromptBlocks = useCallback(
    async (
      messageId: string | undefined,
      questionId: string | undefined,
      blockType: string | undefined,
      blockIndex: number,
      content: ChatInteractRequestContentBlock[]
    ) => {
      const promptKey = getPromptKey(messageId, questionId, blockType, blockIndex);
      setPromptSubmissionState((prev) => ({
        ...prev,
        [promptKey]: { pending: true }
      }));
      try {
        await submitBlocks(messageId, questionId, blockType, content);
        setPromptSubmissionState((prev) => ({
          ...prev,
          [promptKey]: { pending: false }
        }));
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to send reply';
        setPromptSubmissionState((prev) => ({
          ...prev,
          [promptKey]: { pending: false, error: message }
        }));
      }
    },
    [submitBlocks]
  );

  const submitSingleChoicePrompt = useCallback(
    async (event: ChatMessageContentEvent, block: ChatChoicePromptBlock, blockIndex: number, option: ChatChoiceOptionRecord) => {
      await submitPromptBlocks(getPromptMessageId(event), block.questionId, block.type, blockIndex, [
        {
          type: 'choiceResponse',
          questionId: block.questionId,
          responseKind: 'choice',
          choiceId: option.id,
          label: option.label
        }
      ]);
    },
    [submitPromptBlocks]
  );

  const submitChoicePromptDraft = useCallback(
    async (event: ChatMessageContentEvent, block: ChatChoicePromptBlock, blockIndex: number) => {
      const promptKey = getPromptKey(getPromptMessageId(event), block.questionId, block.type, blockIndex);
      const draft = promptChoiceDrafts[promptKey] ?? { selectedIds: [], useFreeform: false, freeformText: '' };
      const selectionMode = block.selectionMode === 'multi' ? 'multi' : 'single';
      if (draft.useFreeform) {
        const text = draft.freeformText.trim();
        if (!text) {
          setPromptSubmissionState((prev) => ({
            ...prev,
            [promptKey]: { pending: false, error: 'A custom response is required.' }
          }));
          return;
        }
        await submitPromptBlocks(getPromptMessageId(event), block.questionId, block.type, blockIndex, [
          {
            type: 'choiceResponse',
            questionId: block.questionId,
            responseKind: 'freeform',
            text
          }
        ]);
        return;
      }

      const selectedIds = draft.selectedIds.filter(Boolean);
      const choices = Array.isArray(block.choices) ? block.choices : [];
      const selectedChoices = choices.filter((choice) => choice.id && selectedIds.includes(choice.id));
      const minSelections = typeof block.minSelections === 'number' ? block.minSelections : 1;
      const maxSelections =
        typeof block.maxSelections === 'number'
          ? block.maxSelections
          : selectionMode === 'single'
            ? 1
            : undefined;
      if (selectedChoices.length < minSelections) {
        setPromptSubmissionState((prev) => ({
          ...prev,
          [promptKey]: { pending: false, error: `Select at least ${minSelections} option${minSelections === 1 ? '' : 's'}.` }
        }));
        return;
      }
      if (typeof maxSelections === 'number' && selectedChoices.length > maxSelections) {
        setPromptSubmissionState((prev) => ({
          ...prev,
          [promptKey]: { pending: false, error: `Select no more than ${maxSelections} option${maxSelections === 1 ? '' : 's'}.` }
        }));
        return;
      }
      if (selectionMode === 'single' && selectedChoices.length === 1) {
        await submitPromptBlocks(getPromptMessageId(event), block.questionId, block.type, blockIndex, [
          {
            type: 'choiceResponse',
            questionId: block.questionId,
            responseKind: 'choice',
            choiceId: selectedChoices[0].id,
            label: selectedChoices[0].label
          }
        ]);
        return;
      }
      await submitPromptBlocks(getPromptMessageId(event), block.questionId, block.type, blockIndex, [
        {
          type: 'choiceResponse',
          questionId: block.questionId,
          responseKind: 'choice',
          choiceIds: selectedChoices.map((choice) => choice.id ?? ''),
          labels: selectedChoices.map((choice) => choice.label ?? choice.id ?? '')
        }
      ]);
    },
    [promptChoiceDrafts, submitPromptBlocks]
  );

  const submitConfirmationPrompt = useCallback(
    async (event: ChatMessageContentEvent, block: ChatConfirmationPromptBlock, blockIndex: number, confirmed: boolean) => {
      await submitPromptBlocks(getPromptMessageId(event), block.questionId, block.type, blockIndex, [
        {
          type: 'confirmationResponse',
          questionId: block.questionId,
          decision: confirmed ? 'confirm' : 'cancel'
        }
      ]);
    },
    [submitPromptBlocks]
  );

  const submitInputPrompt = useCallback(
    async (event: ChatMessageContentEvent, block: ChatInputPromptBlock, blockIndex: number) => {
      const promptKey = getPromptKey(getPromptMessageId(event), block.questionId, block.type, blockIndex);
      const rawValue = promptInputDrafts[promptKey] ?? '';
      const value = rawValue.trim();
      if (!value && !block.allowEmpty) {
        setPromptSubmissionState((prev) => ({
          ...prev,
          [promptKey]: { pending: false, error: 'A response is required.' }
        }));
        return;
      }
      await submitPromptBlocks(getPromptMessageId(event), block.questionId, block.type, blockIndex, [
        {
          type: 'inputResponse',
          questionId: block.questionId,
          text: value
        }
      ]);
      setPromptInputDrafts((prev) => ({
        ...prev,
        [promptKey]: ''
      }));
    },
    [promptInputDrafts, submitPromptBlocks]
  );

  const submitFormPrompt = useCallback(
    async (event: ChatMessageContentEvent, block: ChatFormPromptBlock, blockIndex: number) => {
      const promptKey = getPromptKey(getPromptMessageId(event), block.questionId, block.type, blockIndex);
      const draft = promptFormDrafts[promptKey] ?? {};
      const fields = Array.isArray(block.fields) ? (block.fields as ChatFormFieldRecord[]) : [];
      const missingField = fields.find((field) => field.required && !(draft[field.id ?? ''] ?? '').trim());
      if (missingField) {
        setPromptSubmissionState((prev) => ({
          ...prev,
          [promptKey]: { pending: false, error: `${missingField.label ?? 'A field'} is required.` }
        }));
        return;
      }
      await submitPromptBlocks(getPromptMessageId(event), block.questionId, block.type, blockIndex, [
        {
          type: 'formResponse',
          questionId: block.questionId,
          fields: fields.map((field) => ({
            fieldId: field.id,
            valueText: draft[field.id ?? ''] ?? ''
          }))
        }
      ]);
    },
    [promptFormDrafts, submitPromptBlocks]
  );

  return {
    promptChoiceDrafts,
    promptInputDrafts,
    promptFormDrafts,
    promptSubmissionState,
    setPromptChoiceDrafts,
    setPromptInputDrafts,
    setPromptFormDrafts,
    submitSingleChoicePrompt,
    submitChoicePromptDraft,
    submitConfirmationPrompt,
    submitInputPrompt,
    submitFormPrompt
  };
};
